import { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import { searchAllRecords } from '@ctw/shared/api/fqs-rest/search-helpers';
import type { CTWRequestContext, CTWState } from '@ctw/shared/context/ctw-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { QUERY_KEY_BINARIES_BY_ID } from '@ctw/shared/utils/query-keys';
import { orderBy } from 'lodash-es';

// Returns BinaryModel[], sorted by createdAt (newest first).
export const useBinaries = (binaryIds: Array<string>) =>
  useCtwQuery({
    queryId: QUERY_KEY_BINARIES_BY_ID,
    queryKey: [binaryIds],
    queryFn: async ({ requestContext, ctwFetch }) => {
      if (binaryIds.length === 0) {
        return [];
      }

      const binaries = await searchAllRecords(
        ctwFetch,
        {
          env: requestContext.env,
          builderId: requestContext.builderId,
          authToken: requestContext.authToken,
        },
        'Binary',
        {
          _id: binaryIds.join(','),
        },
      );
      return orderBy(
        binaries.map((binary) => new BinaryModel(binary)),
        'createdAt',
        'desc',
      );
    },
  });

interface GetBinaryByIdOptions {
  binaryId: string;
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
}

export const getBinaryById = async ({
  binaryId,
  ctwFetch,
  requestContext,
}: GetBinaryByIdOptions): Promise<BinaryModel | undefined> => {
  const binaries = await searchAllRecords(
    ctwFetch,
    {
      env: requestContext.env,
      builderId: requestContext.builderId,
      authToken: requestContext.authToken,
    },
    'Binary',
    {
      _id: binaryId,
    },
  );

  return binaries.map((binary) => new BinaryModel(binary)).find((binary) => binary.id === binaryId);
};

interface GetBinariesByIdsOptions {
  binaryIds: Array<string>;
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
}

export const getBinariesByIds = async ({
  binaryIds,
  ctwFetch,
  requestContext,
}: GetBinariesByIdsOptions): Promise<Array<BinaryModel>> => {
  const binaries = await searchAllRecords(
    ctwFetch,
    {
      env: requestContext.env,
      builderId: requestContext.builderId,
      authToken: requestContext.authToken,
    },
    'Binary',
    {
      _id: binaryIds.join(','),
    },
  );

  return binaries.map((binary) => new BinaryModel(binary));
};
